import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { useGetPaymentMethod } from '@api/lendingAPI/useGetPaymentMethod';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleRight } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { useQuery } from '@tanstack/react-query';
import { differenceInDays, format } from 'date-fns';
import { de as deLocale } from 'date-fns/locale/de';
import { enGB as enLocale } from 'date-fns/locale/en-GB';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import DateInIcon from './DateInIcon';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TProductType } from '../../../dashboard/Dashboard.types';
import { SHOW_ACCOUNTS_SCHEDULED_REPAYMENTS_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import DashboardCTA from '../../dashboardComponents/DashboardCTA/DashboardCTA';

const mapStateToProps = (state: RootState) => ({
    locale: state.siteContext.locale
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    showSchedule: (productType: TProductType, accountId: string) =>
        dispatch({
            type: SHOW_ACCOUNTS_SCHEDULED_REPAYMENTS_MODAL,
            productType,
            accountId
        })
});

type InjectedProps = ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> & {
        className?: string;
        accountId: string;
    };

const FutureRepayments = ({
    showSchedule,
    locale,
    className,
    accountId
}: InjectedProps) => {
    const stateKey = useStateKey();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    const {
        data: paymentMethod,
        isLoading: paymentMethodLoading,
        isError: paymentMethodError
    } = useGetPaymentMethod();

    if (isLoading || isError || paymentMethodLoading || paymentMethodError)
        return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const scheduledPayments = account.scheduled_payments;

    if (!scheduledPayments) return null;
    const productType = account.product_type!;

    const nextRepaymentDate = new Date(
        account.scheduled_payments!.schedule![0].date
    );
    const daysToRepayment = differenceInDays(
        nextRepaymentDate,
        // Get the date without a timestamp to calculate the number of full days
        new Date().toISOString().split('T')[0]
    );
    const nextRepaymentAmount = account.scheduled_payments!.schedule![0].total;

    let dateFnsLocal = enLocale;
    if (locale === 'de-DE') {
        dateFnsLocal = deLocale;
    }

    const month = format(nextRepaymentDate, 'MMM', { locale: dateFnsLocal });
    const date = format(nextRepaymentDate, 'd');

    const hasActiveDirectDebit =
        paymentMethod &&
        paymentMethod.data.find(
            (paymentMethod) => paymentMethod.payment_method_type === 'card'
        );

    return (
        <>
            <DashboardCTA
                title={
                    <FormattedMessage
                        id="_.outstanding_view.futureRepaymentsTitle"
                        defaultMessage="Next repayment"
                    />
                }
                icon={<DateInIcon month={month} date={date} />}
                data-testid="future-repayments"
                className={className}
                description={
                    !hasActiveDirectDebit && getCountry() === 'DE' ? (
                        <FormattedMessage
                            id="_.outstanding_view.manualRepayments"
                            defaultMessage="You do not have an active direct debit procedure. Please transfer your next repayment of {repaymentAmount} manually to iwoca by {repaymentDate}."
                            values={{
                                repaymentAmount: (
                                    <strong>
                                        <FormattedCurrency
                                            value={nextRepaymentAmount}
                                        />
                                    </strong>
                                ),
                                repaymentDate: (
                                    <strong>
                                        {date} {month}
                                    </strong>
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.outstanding_view.futureRepayments"
                            defaultMessage="We'll automatically take your next repayment of {repaymentAmount} in {timeToRepayment} days"
                            values={{
                                repaymentAmount: (
                                    <strong>
                                        <FormattedCurrency
                                            value={nextRepaymentAmount}
                                        />
                                    </strong>
                                ),
                                timeToRepayment: (
                                    <strong>{daysToRepayment}</strong>
                                )
                            }}
                        />
                    )
                }
                cta={
                    <Button
                        variant="tertiary"
                        data-testid="check-your-schedule"
                        onClick={() => {
                            showSchedule(productType, accountId);
                            tracking.ampli.checkYourScheduleClicked({
                                account_id: account.account_id!
                            });
                            if (getCountry() === 'DE') {
                                tracking.ampli.dashboardActionClicked({
                                    name: 'Check your schedule'
                                });
                            }
                        }}
                        className="flex-wrap p-0 text-s hover:!bg-transparent hover:underline"
                    >
                        <FormattedMessage
                            id="_.outstanding_view.futureRepaymentsCTA"
                            defaultMessage="Check your schedule"
                        />
                        <IconAngleRight />
                    </Button>
                }
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(FutureRepayments);
